/**
 * Name Mismatch Utilities — KYC Establishment-Type-Aware Verification
 *
 * Encapsulates the business logic for checking name mismatches between
 * cheque account holder name and reference names (firm/PAN) based on
 * establishment type.
 *
 * Rules:
 *  - PROPRIETORSHIP: Dual-check (firm → fallback to PAN) — alert only if both fail
 *  - PARTNERSHIP / COMPANY: Single-check (PAN only) — alert if fails
 */

import { isNameMatch } from './kyc-name-match';

export type EstablishmentType = 'PROPRIETORSHIP' | 'PARTNERSHIP' | 'COMPANY' | 'OTHER';

export interface NameMismatchConfig {
  establishmentType: EstablishmentType;
  firmName?: string;
  panName?: string;
  chequeName?: string;
}

export interface NameMismatchResult {
  showAlert: boolean;
  message: string;
  chequeMatchesFirm: boolean;
  chequeMatchesPan: boolean;
}

/**
 * Resolve establishment type from raw string input.
 */
export function resolveEstablishmentType(type: string): EstablishmentType {
  const normalized = String(type || '').trim().toUpperCase();
  if (normalized === 'PROPRIETORSHIP') return 'PROPRIETORSHIP';
  if (normalized === 'PARTNERSHIP') return 'PARTNERSHIP';
  if (normalized === 'COMPANY') return 'COMPANY';
  return 'OTHER';
}

/**
 * Check name mismatch based on establishment type.
 * Uses the KYC matcher (`isNameMatch`) internally.
 *
 * @param config Configuration with establishment type and names
 * @returns Result with alert flag, message, and match details
 *
 * @example
 * checkNameMismatch({
 *   establishmentType: 'PROPRIETORSHIP',
 *   firmName: 'John\'s Shop',
 *   panName: 'JOHN DOE',
 *   chequeName: 'JOHN DOE'
 * })
 * // PROPRIETORSHIP firm matches → no alert, even if PAN differs
 *
 * checkNameMismatch({
 *   establishmentType: 'PROPRIETORSHIP',
 *   firmName: 'John\'s Shop',
 *   panName: 'JOHN DOE',
 *   chequeName: 'JANE DOE'
 * })
 * // Both firm and PAN fail → alert shown
 *
 * checkNameMismatch({
 *   establishmentType: 'PARTNERSHIP',
 *   panName: 'JOHN DOE',
 *   chequeName: 'JANE DOE'
 * })
 * // PAN mismatch → alert shown
 */
export function checkNameMismatch(config: NameMismatchConfig): NameMismatchResult {
  const { panName = '', chequeName = '' } = config;
  const panTrimmed = panName.trim();
  const chequeTrimmed = chequeName.trim();

  const chequeMatchesPan = panTrimmed && chequeTrimmed
      ? isNameMatch(panTrimmed, chequeTrimmed)
      : false;
  const chequeMatchesFirm = chequeMatchesPan;

  // Determine alert state based on establishment type
  const message = chequeMatchesPan ? '' :
      "Bank account name doesn't match your PAN.";

  return {
    showAlert : !chequeMatchesPan,
    message,
    chequeMatchesFirm,
    chequeMatchesPan,
  };
}
